(() => {
  const money = (cents, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currencyCode }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  class PianziProductGallery {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-pz-slide]')];
      this.thumbs = [...root.querySelectorAll('[data-pz-thumb]')];
      this.currentEl = root.querySelector('[data-pz-current]');
      this.loop = root.dataset.loop === 'true';
      this.hover = root.dataset.hoverThumbnails === 'true';
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.pointerStartX = null;
      this.root.pzGallery = this;
      this.bind();
    }
    bind() {
      this.root.querySelector('[data-pz-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.root.querySelector('[data-pz-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.root.querySelector('[data-pz-thumb-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.root.querySelector('[data-pz-thumb-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.go(index));
        if (this.hover) thumb.addEventListener('mouseenter', () => this.go(index));
      });
      const stage = this.root.querySelector('[data-pz-stage]');
      stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
        if (event.key === 'ArrowRight') this.go(this.index + 1);
      });
      stage?.addEventListener('pointerdown', (event) => { this.pointerStartX = event.clientX; });
      stage?.addEventListener('pointerup', (event) => {
        if (this.pointerStartX === null) return;
        const delta = event.clientX - this.pointerStartX;
        if (Math.abs(delta) > 45) this.go(this.index + (delta < 0 ? 1 : -1));
        this.pointerStartX = null;
      });
    }
    goToMedia(mediaId) {
      if (mediaId == null) return false;
      const wanted = String(mediaId);
      const nextIndex = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === wanted);
      if (nextIndex < 0) return false;
      this.go(nextIndex);
      return true;
    }

    go(nextIndex) {
      if (!this.slides.length) return;
      if (this.loop) nextIndex = (nextIndex + this.slides.length) % this.slides.length;
      else nextIndex = Math.max(0, Math.min(nextIndex, this.slides.length - 1));
      this.slides[this.index]?.classList.remove('is-active');
      this.slides[this.index]?.setAttribute('aria-hidden', 'true');
      this.thumbs[this.index]?.classList.remove('is-active');
      this.thumbs[this.index]?.setAttribute('aria-current', 'false');
      this.index = nextIndex;
      this.slides[this.index]?.classList.add('is-active');
      this.slides[this.index]?.setAttribute('aria-hidden', 'false');
      this.thumbs[this.index]?.classList.add('is-active');
      this.thumbs[this.index]?.setAttribute('aria-current', 'true');
      this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (this.currentEl) this.currentEl.textContent = String(this.index + 1);
      this.slides.forEach((slide, index) => { if (index !== this.index) slide.querySelectorAll('video').forEach((video) => video.pause()); });
    }
  }

  class PianziCustomSelect {
    constructor(details, nativeSelect) {
      this.details = details;
      this.nativeSelect = nativeSelect;
      this.label = details.querySelector('[data-pz-custom-select-label]');
      this.options = [...details.querySelectorAll('[data-pz-custom-option]')];
      this.bind();
      this.sync();
    }
    bind() {
      this.options.forEach((button) => button.addEventListener('click', () => {
        if (button.disabled) return;
        this.nativeSelect.value = button.dataset.value;
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.details.removeAttribute('open');
        this.sync();
      }));
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.details.contains(event.target)) this.details.removeAttribute('open');
      });
      this.details.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { this.details.removeAttribute('open'); this.details.querySelector('summary')?.focus(); }
      });
      const summary = this.details.querySelector('summary');
      summary?.addEventListener('click', (event) => {
        event.stopPropagation();
      });
      this.nativeSelect.addEventListener('change', () => this.sync());
    }
    sync() {
      const selected = this.nativeSelect.selectedOptions?.[0];
      if (this.label) this.label.textContent = selected?.textContent?.replace(/\s+—\s+Sold out\s*$/, '').trim() || 'Choose an option';
      this.options.forEach((button) => {
        const active = button.dataset.value === this.nativeSelect.value;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-selected', String(active));
      });
    }
  }

  class PianziProduct {
    constructor(root) {
      this.root = root;
      this.variants = JSON.parse(root.querySelector('[data-pz-variants-json]')?.textContent || '[]');
      this.variantMedia = JSON.parse(root.querySelector('[data-pz-variant-media-json]')?.textContent || '{}');
      this.gallery = root.querySelector('[data-pz-gallery]')?.pzGallery || null;
      this.inputs = [...root.querySelectorAll('[data-pz-option-input]')];
      this.variantInput = root.querySelector('[data-pz-variant-id]');
      this.mainSubmit = root.querySelector('[data-pz-main-submit]');
      this.stickyBar = root.querySelector('[data-pz-sticky-bar]');
      this.stickySelect = root.querySelector('[data-pz-sticky-variant-select]');
      this.currency = this.variants[0]?.price != null ? (window.Shopify?.currency?.active || 'USD') : 'USD';
      this.addLabel = root.dataset.addLabel || 'Add to cart';
      this.stickyAddLabel = root.dataset.stickyAddLabel || this.addLabel;
      this.lowInventoryThreshold = Number(root.dataset.lowInventoryThreshold || 5);
      this.currentVariant = this.variants.find((v) => String(v.id) === this.variantInput?.value) || this.variants[0];
      this.bind();
      this.updateAvailability();
      this.setupStickyObserver();
      this.syncGalleryToVariant(this.currentVariant, false);
    }

    bind() {
      this.inputs.forEach((input) => input.addEventListener('change', () => this.onOptionsChange()));
      this.stickySelect?.addEventListener('change', () => {
        const variant = this.variants.find((item) => String(item.id) === this.stickySelect.value);
        if (variant) this.selectVariantOptions(variant);
      });
      this.root.querySelector('[data-pz-sticky-submit]')?.addEventListener('click', () => this.mainSubmit?.click());
      this.root.querySelector('[data-pz-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
      this.root.querySelector('[data-pz-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
    }

    selectedOptions() {
      const values = [];
      this.root.querySelectorAll('[data-pz-option-group]').forEach((group) => {
        values[Number(group.dataset.optionPosition) - 1] = group.querySelector('[data-pz-option-input]:checked')?.value;
      });
      return values;
    }

    onOptionsChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
      this.setVariant(variant || null);
    }

    selectVariantOptions(variant) {
      variant.options.forEach((value, index) => {
        const input = this.inputs.find((item) => Number(item.dataset.optionPosition) === index + 1 && item.value === value);
        if (input) input.checked = true;
      });
      this.setVariant(variant);
    }

    setVariant(variant) {
      this.currentVariant = variant;
      if (variant && this.variantInput) this.variantInput.value = variant.id;
      this.updateOptionLabels();
      this.updateAvailability();
      this.updatePrice();
      this.syncGalleryToVariant(variant, true);
      if (variant) {
        if (this.stickySelect) { this.stickySelect.value = String(variant.id); this.stickySelect.dispatchEvent(new Event('pz:sync')); }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    syncGalleryToVariant(variant, announce = true) {
      if (!variant) return;
      const mediaId = this.variantMedia[String(variant.id)] || variant.featured_media?.id || variant.featured_image?.id || variant.featured_image?.media_id;
      if (!mediaId) return;
      if (!this.gallery) this.gallery = this.root.querySelector('[data-pz-gallery]')?.pzGallery || null;
      const changed = this.gallery?.goToMedia(mediaId);
      if (changed && announce) {
        const status = this.root.querySelector('[data-pz-live-status]');
        if (status) status.textContent = `${variant.title} selected. Product image updated.`;
      }
    }

    updateOptionLabels() {
      this.root.querySelectorAll('[data-pz-option-group]').forEach((group) => {
        const selected = group.querySelector('[data-pz-option-input]:checked');
        const label = this.root.querySelector(`[data-pz-option-label="${group.dataset.optionPosition}"]`);
        if (label) label.textContent = selected?.value || '';
      });
      const stickyLabel = this.root.querySelector('[data-pz-sticky-option-label]');
      if (stickyLabel) stickyLabel.textContent = this.currentVariant?.title || 'Unavailable';
    }

    updatePrice() {
      const variant = this.currentVariant;
      const current = this.root.querySelector('[data-pz-current-price]');
      const compare = this.root.querySelector('[data-pz-compare-price]');
      const sticky = this.root.querySelector('[data-pz-sticky-price]');
      const buttonPrice = this.root.querySelector('[data-pz-button-price]');
      if (!variant) return;
      const priceText = money(variant.price, this.currency);
      if (current) current.textContent = priceText;
      if (sticky) sticky.textContent = priceText;
      if (buttonPrice && buttonPrice.textContent.trim()) buttonPrice.textContent = ` — ${priceText}`;
      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.hidden = false;
          compare.textContent = money(variant.compare_at_price, this.currency);
        } else compare.hidden = true;
      }
    }

    updateAvailability() {
      const variant = this.currentVariant;
      const available = Boolean(variant?.available);
      const mainLabel = this.root.querySelector('[data-pz-add-label]');
      const stickyLabel = this.root.querySelector('[data-pz-sticky-label]');
      const stickySubmit = this.root.querySelector('[data-pz-sticky-submit]');
      if (this.mainSubmit) this.mainSubmit.disabled = !available;
      if (stickySubmit) stickySubmit.disabled = !available;
      if (mainLabel) mainLabel.textContent = variant ? (available ? this.addLabel : 'Sold out') : 'Unavailable';
      if (stickyLabel) stickyLabel.textContent = variant ? (available ? this.stickyAddLabel : 'Sold out') : 'Unavailable';

      this.inputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const chosen = this.selectedOptions();
        chosen[position] = input.value;
        const possible = this.variants.some((item) => item.available && item.options.every((value, index) => !chosen[index] || value === chosen[index]));
        input.disabled = !possible;
        input.nextElementSibling?.classList.toggle('is-unavailable', !possible);
      });

      const inventory = this.root.querySelector('[data-pz-inventory]');
      const inventoryText = this.root.querySelector('[data-pz-inventory-text]');
      const stickyInventory = this.root.querySelector('[data-pz-sticky-inventory]');
      const qty = Number(variant?.inventory_quantity || 0);
      const managed = Boolean(variant?.inventory_management);
      const show = available && managed && qty > 0 && qty <= this.lowInventoryThreshold;
      if (inventory) {
        inventory.hidden = !show;
        if (inventoryText && show) inventoryText.textContent = `Only ${qty} left`;
      }
      if (stickyInventory) {
        stickyInventory.hidden = !show;
        if (show) stickyInventory.textContent = `Only ${qty} left`;
      }
    }

    changeQuantity(delta) {
      const input = this.root.querySelector('[data-pz-quantity-input]');
      if (!input) return;
      input.value = Math.max(1, Number(input.value || 1) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setupStickyObserver() {
      if (!this.stickyBar || !this.mainSubmit) return;
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickyBar.classList.toggle('is-visible', show);
        this.stickyBar.setAttribute('aria-hidden', String(!show));
      }, { threshold: 0 });
      observer.observe(this.mainSubmit);
    }
  }


  class PianziCompleteLook {
    constructor(root) {
      this.root = root;
      this.cards = [...root.querySelectorAll('[data-pz-look-card]')];
      this.button = root.querySelector('[data-pz-look-submit]');
      this.label = root.querySelector('[data-pz-look-button-label]');
      this.total = root.querySelector('[data-pz-look-total]');
      this.count = root.querySelector('[data-pz-look-count]');
      this.message = root.querySelector('[data-pz-look-message]');
      this.currency = window.Shopify?.currency?.active || 'USD';
      this.baseLabel = this.label?.textContent?.trim() || 'Add selected items';
      this.bind();
      this.update();
    }

    bind() {
      this.cards.forEach((card) => {
        card.querySelector('[data-pz-look-check]')?.addEventListener('change', () => this.update());
        card.querySelector('[data-pz-look-variant]')?.addEventListener('change', (event) => {
          const option = event.target.selectedOptions[0];
          const checkbox = card.querySelector('[data-pz-look-check]');
          const price = card.querySelector('[data-pz-look-price]');
          const image = card.querySelector('[data-pz-look-image]');
          if (checkbox) checkbox.value = event.target.value;
          if (price && option?.dataset.price) price.textContent = money(Number(option.dataset.price), this.currency);
          if (image && option?.dataset.image) image.src = option.dataset.image;
          this.update();
        });
      });
      this.button?.addEventListener('click', () => this.addSelected());
    }

    selections() {
      return this.cards.flatMap((card) => {
        const check = card.querySelector('[data-pz-look-check]');
        const select = card.querySelector('[data-pz-look-variant]');
        if (!check?.checked || check.disabled) return [];
        const selectedOption = select?.selectedOptions?.[0];
        return [{ id: Number(select?.value || check.value), quantity: 1, price: Number(selectedOption?.dataset.price || 0) }];
      });
    }

    update() {
      const items = this.selections();
      const total = items.reduce((sum, item) => sum + item.price, 0);
      if (this.button) this.button.disabled = items.length === 0;
      if (this.count) this.count.textContent = `${items.length} selected`;
      if (this.total) this.total.textContent = items.length ? ` — ${money(total, this.currency)}` : '';
      if (this.message) { this.message.textContent = ''; this.message.classList.remove('is-error'); }
    }


    applyCartCount(count, cartData = null, addResponse = null) {
      const safeCount = Math.max(0, Number(count) || 0);

      // Use the rollout header's own live controller whenever it exists. This is
      // the most reliable path because it updates the exact badge/text elements
      // created by the active header section instead of guessing at selectors.
      document.querySelectorAll('[data-pianzi-rollout]').forEach((header) => {
        const instance = header.pianziRolloutInstance;
        if (instance && typeof instance.updateCartCount === 'function') {
          instance.updateCartCount(safeCount);
        }
      });

      // Direct DOM fallback for the active PIANZI header.
      document.querySelectorAll('[data-pianzi-cart-count]').forEach((badge) => {
        badge.textContent = String(safeCount);
        badge.removeAttribute('hidden');
        badge.classList.remove('is-hidden');
        badge.setAttribute('aria-hidden', 'true');
        badge.style.removeProperty('display');
      });

      document.querySelectorAll('[data-pianzi-cart-count-text]').forEach((text) => {
        text.textContent = `(${safeCount})`;
      });

      document.querySelectorAll('[data-pianzi-cart-button]').forEach((button) => {
        button.setAttribute('aria-label', `Cart with ${safeCount} ${safeCount === 1 ? 'item' : 'items'}`);
      });

      // Dawn/default-header fallback.
      document.querySelectorAll('#cart-icon-bubble, [data-cart-icon-bubble], .pz-header__cart').forEach((icon) => {
        let bubble = icon.querySelector('.cart-count-bubble, [data-cart-count], .pz-header__cart-count');
        if (safeCount > 0) {
          if (!bubble && icon.id === 'cart-icon-bubble') {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
            icon.appendChild(bubble);
          }
          const visible = bubble?.querySelector('[aria-hidden="true"]') || bubble;
          if (visible) visible.textContent = safeCount < 100 ? String(safeCount) : '';
          const hidden = bubble?.querySelector('.visually-hidden');
          if (hidden) hidden.textContent = `${safeCount} item${safeCount === 1 ? '' : 's'} in cart`;
          if (bubble) bubble.hidden = false;
        } else if (bubble) {
          bubble.remove();
        }
      });

      const payload = {
        source: 'pianzi-complete-look',
        cartData: cartData || { item_count: safeCount },
        cart: cartData || { item_count: safeCount },
        item_count: safeCount,
        response: addResponse
      };

      // Publish through Dawn when its pub/sub globals are available.
      try {
        const pub = typeof publish === 'function' ? publish : window.publish;
        const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
        if (typeof pub === 'function' && events?.cartUpdate) pub(events.cartUpdate, payload);
      } catch (error) {
        console.warn('PIANZI Dawn cart event could not be published:', error);
      }

      ['cart:updated', 'cart:update', 'cart:refresh', 'cart-change', 'product:added'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
      });

      window.dispatchEvent(new CustomEvent('pianzi:cart-count-updated', {
        detail: { count: safeCount, cart: cartData, source: 'pianzi-complete-look' }
      }));
    }

    async refreshCartCount(addResponse, optimisticCount = null) {
      if (optimisticCount !== null) this.applyCartCount(optimisticCount, null, addResponse);

      try {
        const base = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        const separator = base.includes('?') ? '&' : '?';
        const response = await fetch(`${base}${separator}_=${Date.now()}`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache'
          }
        });
        if (!response.ok) throw new Error(`Cart request returned ${response.status}`);
        const cartData = await response.json();
        const count = Number(cartData.item_count || 0);
        this.applyCartCount(count, cartData, addResponse);

        // Re-apply after cart drawer rendering/section replacement has settled.
        window.setTimeout(() => this.applyCartCount(count, cartData, addResponse), 120);
        window.setTimeout(() => this.applyCartCount(count, cartData, addResponse), 600);
        window.setTimeout(() => {
          document.querySelectorAll('[data-pianzi-rollout]').forEach((header) => {
            header.pianziRolloutInstance?.refreshCartCount?.();
          });
        }, 900);
      } catch (error) {
        console.warn('PIANZI could not refresh the cart count:', error);
      }
    }

    async addSelected() {
      const items = this.selections();
      if (!items.length || !this.button) return;

      // Use Dawn's real cart drawer as the primary cart UI. This matches the
      // native product-form flow and lets Shopify return the exact sections the
      // drawer needs before it opens.
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      this.button.disabled = true;
      this.button.classList.add('is-loading');
      this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
      if (this.label) this.label.textContent = 'Adding…';
      if (this.message) {
        this.message.textContent = '';
        this.message.classList.remove('is-error');
      }

      try {
        const config = typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : {
              method: 'POST',
              headers: { Accept: 'application/javascript' }
            };

        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();

        // Shopify's multi-item Ajax syntax. Using FormData here mirrors Dawn's
        // native product form instead of sending a custom JSON cart request.
        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, String(item.id));
          formData.append(`items[${index}][quantity]`, String(item.quantity || 1));
        });

        if (cart?.getSectionsToRender) {
          const sectionIds = cart
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean);

          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }

        config.body = formData;

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add selected items.');
        }

        // Let Dawn announce the cart update before rendering, exactly like its
        // native product-form.js flow.
        try {
          const pub = typeof publish === 'function' ? publish : window.publish;
          const events = typeof PUB_SUB_EVENTS !== 'undefined'
            ? PUB_SUB_EVENTS
            : window.PUB_SUB_EVENTS;

          if (typeof pub === 'function' && events?.cartUpdate) {
            await pub(events.cartUpdate, {
              source: 'pianzi-complete-look',
              cartData: data
            });
          }
        } catch (publishError) {
          console.warn('PIANZI cart update event could not be published:', publishError);
        }

        // This is the Rhode-style interaction the section was missing:
        // Shopify-rendered drawer HTML is inserted and CartDrawer.open() runs.
        if (cart?.renderContents && data.sections) {
          cart.renderContents(data);
        } else {
          // A drawer should exist in Dawn, but keep a safe fallback for stores
          // where it has been disabled in Theme Editor.
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        // Synchronize the custom rollout-header count from Shopify after the
        // drawer has rendered. A second refresh happens when the drawer closes.
        await this.refreshCartCount(data, null);

        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.addEventListener(
            'pianzi:cart-drawer-closed',
            () => this.refreshCartCount(data, null),
            { once: true }
          );
        }

        this.cards.forEach((card) => {
          const check = card.querySelector('[data-pz-look-check]');
          if (check) check.checked = false;
        });

        if (this.message) {
          this.message.textContent = `${items.length} item${items.length === 1 ? '' : 's'} added to your cart.`;
        }
      } catch (error) {
        console.error('PIANZI Complete the Look cart error:', error);
        if (this.message) {
          this.message.textContent = error.message || 'Unable to add selected items.';
          this.message.classList.add('is-error');
        }
      } finally {
        this.button.classList.remove('is-loading');
        this.button.querySelector('.loading__spinner')?.classList.add('hidden');
        if (this.label) this.label.textContent = this.baseLabel;
        this.update();
      }
    }
  }

  class PianziEditorialAccordions {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-pz-accordion]'));
      this.singleOpen = root.dataset.singleOpen === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.items.forEach((item) => this.bind(item));
      window.addEventListener('resize', () => this.syncOpenHeights(), { passive: true });
    }

    bind(item) {
      const summary = item.querySelector('summary');
      const reveal = item.querySelector('[data-pz-accordion-reveal]');
      if (!summary || !reveal) return;

      if (item.open) {
        reveal.style.height = 'auto';
        reveal.style.opacity = '1';
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        item.open ? this.close(item) : this.open(item);
      });
    }

    open(item) {
      if (this.singleOpen) {
        this.items.filter((other) => other !== item && other.open).forEach((other) => this.close(other));
      }
      const reveal = item.querySelector('[data-pz-accordion-reveal]');
      if (!reveal) return;
      item.open = true;
      reveal.style.height = '0px';
      reveal.style.opacity = '0';
      const target = reveal.scrollHeight;
      if (this.reducedMotion) {
        reveal.style.height = 'auto';
        reveal.style.opacity = '1';
        return;
      }
      requestAnimationFrame(() => {
        reveal.style.height = `${target}px`;
        reveal.style.opacity = '1';
      });
      const done = (event) => {
        if (event.propertyName !== 'height') return;
        reveal.style.height = 'auto';
        reveal.removeEventListener('transitionend', done);
      };
      reveal.addEventListener('transitionend', done);
    }

    close(item) {
      const reveal = item.querySelector('[data-pz-accordion-reveal]');
      if (!reveal) return;
      if (this.reducedMotion) {
        item.open = false;
        reveal.style.height = '0px';
        reveal.style.opacity = '0';
        return;
      }
      reveal.style.height = `${reveal.scrollHeight}px`;
      reveal.style.opacity = '1';
      requestAnimationFrame(() => {
        reveal.style.height = '0px';
        reveal.style.opacity = '0';
      });
      const done = (event) => {
        if (event.propertyName !== 'height') return;
        item.open = false;
        reveal.removeEventListener('transitionend', done);
      };
      reveal.addEventListener('transitionend', done);
    }

    syncOpenHeights() {
      this.items.forEach((item) => {
        if (!item.open) return;
        const reveal = item.querySelector('[data-pz-accordion-reveal]');
        if (reveal) reveal.style.height = 'auto';
      });
    }
  }



  class PianziMotionPass {
    constructor(root) {
      this.root = root;
      this.enabled = root.dataset.pzMotion === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.items = this.collectItems();
      if (!this.enabled || this.reducedMotion || !('IntersectionObserver' in window)) {
        this.items.forEach((item) => item.classList.add('is-pz-revealed'));
        return;
      }
      this.observe();
    }

    collectItems() {
      const selectors = [
        '.pz-product-gallery',
        '.pz-product-main__header',
        '.pz-editorial-statuses',
        '.pz-variant-picker',
        '.pz-product-main__inventory',
        '.pz-product-main__form',
        '.pz-product-highlights',
        '.pz-product-trust',
        '.pz-complete-look',
        '.pz-product-main__accordions'
      ];
      const seen = new Set();
      const items = [];
      selectors.forEach((selector) => {
        this.root.querySelectorAll(selector).forEach((item) => {
          if (seen.has(item)) return;
          seen.add(item);
          item.setAttribute('data-pz-motion-item', '');
          item.style.setProperty('--pz-motion-delay', `${Math.min(items.length * 45, 270)}ms`);
          items.push(item);
        });
      });
      return items;
    }

    observe() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-pz-revealed');
          this.observer.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
      this.items.forEach((item) => this.observer.observe(item));
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-pz-custom-select]').forEach((details) => {
      if (details.dataset.initialized) return;
      const wrap = details.closest('.pz-complete-look__select-wrap, .pz-sticky-atc__select-wrap');
      const nativeSelect = wrap?.querySelector('select');
      if (nativeSelect) {
        details.dataset.initialized = 'true';
        const custom = new PianziCustomSelect(details, nativeSelect);
        nativeSelect.addEventListener('pz:sync', () => custom.sync());
      }
    });
    scope.querySelectorAll('[data-pz-gallery]').forEach((gallery) => {
      if (!gallery.dataset.initialized) { gallery.dataset.initialized = 'true'; new PianziProductGallery(gallery); }
    });
    scope.querySelectorAll('.pz-product-main').forEach((product) => {
      if (!product.dataset.productInitialized) { product.dataset.productInitialized = 'true'; new PianziProduct(product); }
    });
    scope.querySelectorAll('[data-pz-complete-look]').forEach((look) => {
      if (!look.dataset.initialized) { look.dataset.initialized = 'true'; new PianziCompleteLook(look); }
    });
    scope.querySelectorAll('[data-pz-accordions]').forEach((accordions) => {
      if (!accordions.dataset.initialized) { accordions.dataset.initialized = 'true'; new PianziEditorialAccordions(accordions); }
    });
    scope.querySelectorAll('.pz-product-main').forEach((product) => {
      if (!product.dataset.motionInitialized) { product.dataset.motionInitialized = 'true'; new PianziMotionPass(product); }
    });
  };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
